/**
 * OAuth2 redirect handling for external integrations.
 *
 * Providers now require an HTTPS redirect URI: Spotify only accepts
 * `https://...` plus the loopback literals `127.0.0.1` and `[::1]` since April
 * 2025. A Gladys instance reached at http://192.168.1.50:1443 on the local
 * network can never satisfy that, so a fixed HTTPS page hosted by Gladys is
 * declared as the redirect URI instead, and it bounces the browser back to the
 * instance.
 *
 * The provider hands the `state` parameter back untouched, so it is the only
 * way to carry the address of the instance across the round trip: the state
 * generated by the integration is wrapped with the origin and callback path of
 * this frontend. The redirect page unwraps it and gives the integration state
 * back to the instance, which is the only party able to verify it.
 *
 * Source of the redirect page: https://github.com/GladysAssistant/my.gladysassistant.com
 */

export const OAUTH_REDIRECT_URI = 'https://my.gladysassistant.com/redirect/oauth';

const OAUTH_STATE_VERSION = 1;
// mirrored from MAX_INNER_STATE_LENGTH / MAX_WRAPPED_STATE_LENGTH in
// assets/js/oauth-state.js of GladysAssistant/my.gladysassistant.com, which
// refuses anything above these bounds: better an immediate error on "Connect"
// than a dead end once the user has already consented at the provider. Keep
// both files in sync.
const MAX_INNER_STATE_LENGTH = 1024;
const MAX_WRAPPED_STATE_LENGTH = 4096;

/**
 * @description The generic OAuth2 callback route of an external integration.
 * @param {string} selector - The selector of the external integration.
 * @returns {string} The absolute path of the callback route.
 * @example getOAuthCallbackPath('ext-dev-spotify');
 */
export function getOAuthCallbackPath(selector) {
  return `/dashboard/integration/device/external/${selector}/oauth-callback`;
}

function base64UrlEncode(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * @description Wrap the state of the integration with the address to come back to.
 * @param {object} params - The wrapping parameters.
 * @param {string} params.origin - The origin of this frontend, as the user reaches it.
 * @param {string} params.path - The callback path inside this frontend.
 * @param {string} params.state - The anti-CSRF state generated by the integration.
 * @returns {string} The base64url state to hand to the provider.
 * @example wrapOAuthState({ origin: 'http://192.168.1.50:1443', path: '/dashboard/...', state: 'abc' });
 */
export function wrapOAuthState({ origin, path, state }) {
  return base64UrlEncode(JSON.stringify({ v: OAUTH_STATE_VERSION, origin, path, state }));
}

/**
 * @description Read the anti-CSRF state of an authorize URL. It is mandatory
 * whichever redirect URI is in use: without it the integration has no anti-CSRF
 * protection, and through the redirect page there is no way back either.
 * @param {string} authorizeUrl - The authorize URL returned by the integration.
 * @returns {string} The state generated by the integration.
 * @throws {Error} When the integration did not generate a usable state.
 * @example getAuthorizeUrlState('https://accounts.spotify.com/authorize?state=abc');
 */
export function getAuthorizeUrlState(authorizeUrl) {
  const state = new URL(authorizeUrl).searchParams.get('state');
  if (!state || state.length > MAX_INNER_STATE_LENGTH) {
    throw new Error('EXTERNAL_INTEGRATION_OAUTH_INVALID_STATE');
  }
  return state;
}

/**
 * @description Rewrite the authorize URL built by the integration so its state
 * carries the address of this instance. Everything else in the URL (client_id,
 * scopes, provider specifics) is left untouched: the frontend knows no provider.
 * @param {string} authorizeUrl - The authorize URL returned by the integration.
 * @param {object} params - The wrapping parameters.
 * @param {string} params.origin - The origin of this frontend.
 * @param {string} params.path - The callback path inside this frontend.
 * @returns {string} The authorize URL to open, with a wrapped state.
 * @throws {Error} When the integration did not generate a usable state.
 * @example wrapAuthorizeUrl(url, { origin: window.location.origin, path: getOAuthCallbackPath(selector) });
 */
export function wrapAuthorizeUrl(authorizeUrl, { origin, path }) {
  const url = new URL(authorizeUrl);
  const wrappedState = wrapOAuthState({ origin, path, state: getAuthorizeUrlState(authorizeUrl) });
  if (wrappedState.length > MAX_WRAPPED_STATE_LENGTH) {
    throw new Error('EXTERNAL_INTEGRATION_OAUTH_INVALID_STATE');
  }
  url.searchParams.set('state', wrappedState);
  return url.toString();
}
